<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'

import externalLibsService from '@/services/embeds/plugin/external.libs.service'
import { useEmbedConfigureStore } from '@/stores/embeds/embedConfigure.store'

import { useIdeStore } from '@/stores/embeds/ide.store'

const ideStore = useIdeStore()
const embedConfigStore = useEmbedConfigureStore()

const { selectedLanguage, libsLanguage, getLibsCanEdit } = storeToRefs(ideStore)
const { getOnEditMode, getLanguageMeta } = storeToRefs(embedConfigStore)

const lib = ref<string>('')
const showValidation = ref<boolean>(false)

const selectedLibraries = computed(() => {
  const langObj =
    libsLanguage.value.find(
      (language) => Object.keys(language)[0] === selectedLanguage.value?.language
    ) || {}
  return Object.values(langObj)[0] || []
})

const isLibsSupport = computed(() => {
  if (!selectedLanguage.value || !selectedLanguage.value?.language) return false
  const languageMetaItem: any = getLanguageMeta.value[selectedLanguage.value?.language]
  return languageMetaItem.library ? true : false
})
const isShowLibs = computed(() => {
  return selectedLibraries.value.length > 0
})
const showValidationError = computed(() => {
  return lib.value == '' && showValidation.value
})
/**
 * @description get language display name
 * @param name The language name
 * @returns The language display name
 */
const languageDisplayName = (name: string) => {
  const languageMetaItem: any = getLanguageMeta.value[name]
  return languageMetaItem.langDisplayName || languageMetaItem.langName
}
/**
 * remove the library
 * @param lib The library name
 */
const removeLib = (lib: string) => {
  libsLanguage.value.forEach((language) => {
    if (Object.keys(language)[0] === selectedLanguage.value?.language) {
      language[selectedLanguage.value?.language] = language[
        selectedLanguage.value?.language
      ].filter((item: string) => item !== lib)
    }
  })
}
/**
 * @description add library
 */
const onSubmit = () => {
  showValidation.value = true
  if (showValidationError.value) return

  libsLanguage.value.forEach((language) => {
    if (Object.keys(language)[0] === selectedLanguage.value?.language) {
      if (!language[selectedLanguage.value?.language])
        language[selectedLanguage.value?.language] = []

      const isLibAlreadyExist = language[selectedLanguage.value?.language].find(
        (item: string) => item === lib.value
      )
      if (!isLibAlreadyExist) language[selectedLanguage.value?.language].push(lib.value)
    }
  })

  lib.value = ''
}

onMounted(async () => {
  showValidation.value = false
  await new Promise((resolve) => setTimeout(resolve, 100))
  if (getOnEditMode.value === false) {
    externalLibsService.initLibs()
    externalLibsService.onSettingChange()
  }
})
onBeforeUnmount(async () => {
  if (getOnEditMode.value === false) {
    externalLibsService.destroyLibs()
  }
  await new Promise((resolve) => setTimeout(resolve, 100))
})
</script>

<template>
  <div class="flex h-auto min-h-min w-full flex-col">
    <p :class="['font-thin ', { hidden: isLibsSupport }]">
      {{ languageDisplayName(selectedLanguage?.language as string) }} does not support external
      libraries.
    </p>
    <div v-if="getLibsCanEdit" class="flex flex-col gap-4 pb-5 pt-2">
      <span class="font-semibold leading-6">Add External Library</span>
      <div class="flex w-full flex-col gap-2">
        <div
          class="flex h-12 min-h-fit justify-between gap-1 rounded-md border border-gray-400 p-1"
        >
          <input type="text" class="h-full w-full px-2" v-model="lib" />
          <button
            :disabled="showValidationError || !isLibsSupport"
            @click="onSubmit"
            class="min-w-fit rounded-sm border border-gray-400 p-2 px-2 font-normal leading-6 outline-none"
          >
            Add library
          </button>
        </div>
        <p :class="['error text-left font-thin', { hidden: !showValidationError }]">
          this is a required field
        </p>
      </div>
    </div>
    <div class="flex h-full flex-col gap-4 pb-5">
      <span class="font-semibold leading-6">External Library List</span>
      <div class="h-full max-h-64 w-full overflow-y-auto px-2">
        <ul v-if="isShowLibs" class="flex h-full flex-col gap-2">
          <li
            v-for="lib in selectedLibraries"
            :key="lib"
            class="flex items-baseline justify-between gap-5 py-1"
          >
            <div class="flex gap-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M4 14L12 18L20 14M12 6L4 10L12 14L20 10L12 6Z"
                  stroke="#0A2540"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <p class="truncate font-semibold leading-6">{{ lib }}</p>
            </div>
            <button @click="removeLib(lib)">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20.0008 6C20.2557 6.00028 20.5009 6.09788 20.6862 6.27285C20.8715 6.44782 20.9831 6.68695 20.998 6.94139C21.013 7.19584 20.9302 7.44638 20.7666 7.64183C20.603 7.83729 20.3709 7.9629 20.1178 7.993L20.0008 8H19.9198L19.0008 19C19.0009 19.7652 18.7085 20.5015 18.1836 21.0583C17.6586 21.615 16.9407 21.9501 16.1768 21.995L16.0008 22H8.00085C6.40285 22 5.09685 20.751 5.00885 19.25L5.00384 19.083L4.08085 8H4.00085C3.74597 7.99972 3.50081 7.90212 3.31548 7.72715C3.13014 7.55218 3.01861 7.31305 3.00367 7.05861C2.98874 6.80416 3.07152 6.55362 3.23511 6.35817C3.3987 6.16271 3.63075 6.0371 3.88385 6.007L4.00085 6H20.0008Z"
                  fill="#0A2540"
                />
                <path
                  d="M14.0009 2C14.5313 2 15.04 2.21071 15.4151 2.58579C15.7901 2.96086 16.0009 3.46957 16.0009 4C16.0006 4.25488 15.903 4.50003 15.728 4.68537C15.553 4.8707 15.3139 4.98223 15.0595 4.99717C14.805 5.01211 14.5545 4.92933 14.359 4.76574C14.1636 4.60214 14.038 4.3701 14.0079 4.117L14.0009 4H10.0009L9.99385 4.117C9.96375 4.3701 9.83814 4.60214 9.64269 4.76574C9.44723 4.92933 9.19669 5.01211 8.94225 4.99717C8.68781 4.98223 8.44867 4.8707 8.2737 4.68537C8.09873 4.50003 8.00114 4.25488 8.00085 4C8.00069 3.49542 8.19126 3.00943 8.53435 2.63945C8.87744 2.26947 9.3477 2.04284 9.85085 2.005L10.0009 2H14.0009Z"
                  fill="#0A2540"
                />
                <path
                  d="M20.0008 6C20.2557 6.00028 20.5009 6.09788 20.6862 6.27285C20.8715 6.44782 20.9831 6.68695 20.998 6.94139C21.013 7.19584 20.9302 7.44638 20.7666 7.64183C20.603 7.83729 20.3709 7.9629 20.1178 7.993L20.0008 8H19.9198L19.0008 19C19.0009 19.7652 18.7085 20.5015 18.1836 21.0583C17.6586 21.615 16.9407 21.9501 16.1768 21.995L16.0008 22H8.00085C6.40285 22 5.09685 20.751 5.00885 19.25L5.00384 19.083L4.08085 8H4.00085C3.74597 7.99972 3.50081 7.90212 3.31548 7.72715C3.13014 7.55218 3.01861 7.31305 3.00367 7.05861C2.98874 6.80416 3.07152 6.55362 3.23511 6.35817C3.3987 6.16271 3.63075 6.0371 3.88385 6.007L4.00085 6H20.0008Z"
                  stroke="black"
                />
                <path
                  d="M14.0009 2C14.5313 2 15.04 2.21071 15.4151 2.58579C15.7901 2.96086 16.0009 3.46957 16.0009 4C16.0006 4.25488 15.903 4.50003 15.728 4.68537C15.553 4.8707 15.3139 4.98223 15.0595 4.99717C14.805 5.01211 14.5545 4.92933 14.359 4.76574C14.1636 4.60214 14.038 4.3701 14.0079 4.117L14.0009 4H10.0009L9.99385 4.117C9.96375 4.3701 9.83814 4.60214 9.64269 4.76574C9.44723 4.92933 9.19669 5.01211 8.94225 4.99717C8.68781 4.98223 8.44867 4.8707 8.2737 4.68537C8.09873 4.50003 8.00114 4.25488 8.00085 4C8.00069 3.49542 8.19126 3.00943 8.53435 2.63945C8.87744 2.26947 9.3477 2.04284 9.85085 2.005L10.0009 2H14.0009Z"
                  stroke="black"
                />
              </svg>
            </button>
          </li>
        </ul>
        <p v-else class="font-thin">No libraries added</p>
      </div>
    </div>
  </div>
</template>
